import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../../../types/shared/UUID';
import {
  useCreateInternalOrderContactMutation,
  useListInternalOrderContactsQuery,
  useUpdateInternalOrderContactMutation,
} from '../../../../../../../API/order/contacts/InternalContactsApi';
import { useListPagedAdminUsersQuery } from '../../../../../../../API/administration/AdminUsersAPI';
import {
  eAdminAccountStatus,
  IAdminUserData,
} from '../../../../../../../types/administration/userAdministration/IAdminUser';
import { SelectOption } from '../../../../../../../tailwindComponents/selectTW/interfaces';
import { useCallback, useMemo } from 'react';
import { createDefaultPageData } from '../../../../../../../types/rest/IPageData';
import { checkForMutationSuccess } from '../../../../../../../API/checkForMutationSuccess';

const defaultData = createDefaultPageData<IAdminUserData>();
const closerRoleName = 'closer';

export const useSetCloser = (onSuccess?: () => void) => {
  const { orderId } = useParams<{ orderId: UUID }>();

  // we will need to know all the internal contacts associated with this order
  const {
    data: internalContacts = [],
    error: internalContactsError,
    isLoading: isLoadingInternalContacts,
  } = useListInternalOrderContactsQuery(orderId);

  // these are the admins that could be assigned to this order as an internal contact
  const {
    data: adminUsers = defaultData,
    isLoading: isLoadingAdminUsers,
    error: adminUsersError,
  } = useListPagedAdminUsersQuery({
    fetchAll: true,
    status: eAdminAccountStatus.Active,
  });

  //these are the possible mutations
  const [createInternalContact, { error: postError, isLoading: postLoading, isSuccess: postSuccess }] =
    useCreateInternalOrderContactMutation();
  const [updateInternalContact, { error: updateError, isLoading: updateLoading, isSuccess: updateSuccess }] =
    useUpdateInternalOrderContactMutation();

  const combinedError = adminUsersError ?? internalContactsError ?? postError ?? updateError;
  const combinedIsLoading = isLoadingAdminUsers || isLoadingInternalContacts || postLoading || updateLoading;
  const combinedSuccess = postSuccess || updateSuccess;

  const options: SelectOption[] = useMemo(() => {
    return adminUsers.content.map((user) => ({ label: user.fullName, value: user.id }));
  }, [adminUsers]);

  const onSubmit = useCallback(
    async (option: SelectOption) => {
      // it is possible that the selected admin is already an internal contact.
      // this determines whether we're posting a new contact or updating an existing one
      const previousContact = internalContacts.find((contact) => contact.admin.id === option.value);

      let result;
      if (previousContact) {
        // Check if they already have the closer role
        const hasCloserRole = previousContact.roles.includes(closerRoleName);
        if (!hasCloserRole) {
          result = await updateInternalContact({
            orderId,
            contact: {
              ...previousContact,
              roles: [...previousContact.roles, closerRoleName],
            },
          });
        }
      } else {
        result = await createInternalContact({
          orderId,
          contact: { adminId: `${option.value}`, roles: [closerRoleName] },
        });
      }
      if (result && checkForMutationSuccess(result, 'Adding Closer') && onSuccess) {
        onSuccess();
      }
    },
    [internalContacts, orderId, updateInternalContact, createInternalContact, onSuccess],
  );

  return {
    combinedError,
    combinedIsLoading,
    combinedSuccess,
    options,
    onSubmit,
  };
};
